import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, PenLine, ShieldCheck } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AvatarUpload } from "@/components/profiel/AvatarUpload";
import { PushMeldingen } from "@/components/profiel/PushMeldingen";
import { ApparaatInstellingen } from "@/components/profiel/ApparaatInstellingen";
import { PushApparaten } from "@/components/profiel/PushApparaten";
import { IosChecklist } from "@/components/profiel/IosChecklist";
import { RichTextEditor } from "@/components/email/RichTextEditor";
import { authApi, vertaalApiFout } from "@/lib/api";
import { useAuthStore } from "@/lib/auth-store";
import { StatusChip } from "@/components/shared/StatusChip";
import { formatDatumTijd } from "@/lib/utils";
import type { TweeFASetupAntwoord } from "@/types/api";

export function ProfielPagina() {
  const { t } = useTranslation();
  const gebruiker = useAuthStore((s) => s.gebruiker);
  const setGebruiker = useAuthStore((s) => s.setGebruiker);
  const [tweefaSetup, setTweefaSetup] = useState<TweeFASetupAntwoord | null>(null);
  // Step-up bij een reset van een reeds actief 2FA (S5): het huidige
  // wachtwoord is vereist voordat het bestaande secret vervangen wordt.
  const [stepUpWachtwoord, setStepUpWachtwoord] = useState("");

  const [handtekeningHtml, setHandtekeningHtml] = useState(
    gebruiker?.handtekening_html ?? "",
  );
  const [handtekeningTekst, setHandtekeningTekst] = useState(
    gebruiker?.handtekening_tekst ?? "",
  );

  // Sync state als gebruiker in store verandert
  useEffect(() => {
    setHandtekeningHtml(gebruiker?.handtekening_html ?? "");
    setHandtekeningTekst(gebruiker?.handtekening_tekst ?? "");
  }, [gebruiker?.handtekening_html, gebruiker?.handtekening_tekst]);

  const setup2faMutatie = useMutation({
    mutationFn: () =>
      authApi.setup2FA(
        gebruiker?.twee_factor_actief
          ? { wachtwoord: stepUpWachtwoord }
          : undefined,
      ),
    onSuccess: (resp) => {
      setTweefaSetup(resp);
      setStepUpWachtwoord("");
    },
    onError: (fout) => toast.error(vertaalApiFout(fout)),
  });

  const handtekeningMutatie = useMutation({
    mutationFn: () =>
      authApi.zetHandtekening(
        handtekeningHtml || null,
        handtekeningTekst || null,
      ),
    onSuccess: (g) => {
      setGebruiker(g);
      toast.success(t("profiel.handtekening_opgeslagen"));
    },
    onError: (fout) => toast.error(vertaalApiFout(fout)),
  });

  if (!gebruiker) return null;

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <h1 className="text-[26px] font-bold tracking-tight">
          {t("profiel.titel")}
        </h1>
      </header>

      {/* Profielfoto */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <h2 className="text-[15px] font-semibold">
              {t("profiel.avatar_titel")}
            </h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              {t("profiel.avatar_uitleg")}
            </p>
          </div>
          <AvatarUpload gebruiker={gebruiker} />
        </CardContent>
      </Card>

      {/* Account */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <h2 className="text-[15px] font-semibold">
            {t("profiel.account_titel")}
          </h2>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[14px]">
            <Row label={t("profiel.naam")} value={gebruiker.naam} />
            <Row label={t("profiel.email")} value={gebruiker.email} />
            <Row label={t("profiel.rol")} value={gebruiker.rol} />
            <Row
              label={t("profiel.laatste_login")}
              value={
                gebruiker.laatst_ingelogd_op
                  ? formatDatumTijd(gebruiker.laatst_ingelogd_op)
                  : "—"
              }
            />
          </dl>
          <p className="text-[12.5px] text-muted-foreground">
            Naam en wachtwoord wijzigen komt in een volgende release — voor
            nu via een administrator.
          </p>
        </CardContent>
      </Card>

      {/* Pushmeldingen op dit apparaat (ronde E) */}
      <PushMeldingen />

      {/* Alle apparaten met hun stand (sessie 4): de meting van 09-09
          vond één abonnement terwijl JP drie toestellen gebruikt, en
          niets op dit scherm maakte dat zichtbaar. */}
      <PushApparaten />

      {/* Stappenplan voor iPhone/iPad; toont zichzelf alleen daar. */}
      <IosChecklist />

      {/* Vertrouwd apparaat + portaalgeluid (08-09), ook per apparaat. */}
      <ApparaatInstellingen />

      {/* E-mail handtekening */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-brand-orange-50 text-brand-orange-700 grid place-items-center">
              <PenLine className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="text-[15px] font-semibold">
                {t("profiel.handtekening_titel")}
              </h2>
              <p className="text-[13px] text-muted-foreground mt-0.5">
                {t("profiel.handtekening_uitleg")}
              </p>
            </div>
          </div>
          <RichTextEditor
            value={handtekeningHtml}
            onChange={(html, tekst) => {
              setHandtekeningHtml(html);
              setHandtekeningTekst(tekst);
            }}
            placeholder={t("profiel.handtekening_placeholder")}
            rows={6}
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setHandtekeningHtml("");
                setHandtekeningTekst("");
              }}
              disabled={handtekeningMutatie.isPending}
            >
              {t("profiel.handtekening_wissen")}
            </Button>
            <Button
              onClick={() => handtekeningMutatie.mutate()}
              disabled={handtekeningMutatie.isPending}
            >
              {handtekeningMutatie.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              {t("profiel.handtekening_opslaan")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 2FA */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-brand-cyan-soft text-brand-cyan-dark grid place-items-center">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="text-[15px] font-semibold">
                {t("profiel.tweefa_titel")}
              </h2>
              <div className="mt-1">
                {gebruiker.twee_factor_actief ? (
                  <StatusChip variant="cyan">
                    {t("profiel.tweefa_status_aan")}
                  </StatusChip>
                ) : (
                  <StatusChip variant="amber">
                    {t("profiel.tweefa_status_uit")}
                  </StatusChip>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setup2faMutatie.mutate()}
              disabled={
                setup2faMutatie.isPending ||
                (gebruiker.twee_factor_actief && !stepUpWachtwoord)
              }
            >
              {setup2faMutatie.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              {t("profiel.tweefa_reset")}
            </Button>
          </div>
          {gebruiker.twee_factor_actief && (
            <div className="space-y-1.5">
              <label
                htmlFor="tweefa-stepup"
                className="text-[13px] font-medium"
              >
                {t("profiel.tweefa_reset_stepup_label")}
              </label>
              <Input
                id="tweefa-stepup"
                type="password"
                autoComplete="current-password"
                value={stepUpWachtwoord}
                onChange={(e) => setStepUpWachtwoord(e.target.value)}
                placeholder={t("profiel.tweefa_reset_stepup_placeholder")}
                className="max-w-sm"
              />
            </div>
          )}
          <p className="text-[13px] text-muted-foreground">
            {t("profiel.tweefa_reset_uitleg")}
          </p>

          {tweefaSetup && (
            <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
              <img
                src={tweefaSetup.qr_code_data_url}
                alt="QR code voor authenticator"
                className="h-40 w-40 rounded bg-white p-2 border"
              />
              <div className="text-[12.5px] text-muted-foreground">
                Secret (handmatig invoeren als QR niet werkt):{" "}
                <code className="font-mono text-foreground">
                  {tweefaSetup.secret}
                </code>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </>
  );
}
